/**
 * The unlocked private key, for as long as the tab is open.
 *
 * Memory only — never `localStorage`, never `sessionStorage`, never IndexedDB.
 * The access token is held the same way and for the same reason, but the stakes
 * here are different in kind: a stolen access token expires in fifteen minutes,
 * while this key opens every photograph the account can see, for as long as the
 * account exists. Anything that survives a refresh survives an XSS.
 *
 * The unwrapped key is also handed to `keyVault`, which keeps it in IndexedDB
 * for this device. That is not a contradiction of the paragraph above: it is
 * stored as a non-extractable `CryptoKey`, so the browser will use it and will
 * never give its bytes to script — not to us, and not to anything injected into
 * the page. What survives a refresh is the ability to decrypt on this device,
 * not a copy of the key.
 *
 * Without it, reloading the page locked the keyring and left somebody signed in
 * and looking at photographs they could not open, with no way out but to sign
 * out and sign in again. Nobody was getting a security benefit from that; it
 * read as a bug because it was one.
 */

import {
  forgetPrivateKey,
  recallPrivateKey,
  rememberPrivateKey,
} from "./keyVault.js";
import {
  MediaCryptoError,
  base64ToBytes,
  deriveKeyFromPassword,
  generateKeypair,
  generateSalt,
  unwrapFromPrivateKey,
  unwrapPrivateKey,
  rewrapPrivateKey,
  unwrapWithKey,
  wrapWithKey,
  wrapWithDerivedKey,
  unwrapWithDerivedKey,
  wrapToPublicKey,
  importPublicKey,
  generateSymmetricKey,
  bytesToBase64,
  contentTag,
  encrypt,
  decrypt,
} from "@fc/core";

import { recoveryProof } from "@fc/core";

/** Prove possession without exporting the non-extractable device key. */
export async function proveDeviceKey(challenge: string): Promise<string> {
  if (!privateKey) throw new Error("This device cannot open your photos.");
  return recoveryProof(challenge, privateKey);
}

export interface KeyBundle {
  publicKey: string;
  wrappedPrivateKey: string;
  keySalt: string;
  kdfIterations: number;
}

/** Never serialised, never exported, never leaves this module's closure. */
let privateKey: CryptoKey | null = null;
let publicKey: string | null = null;

/**
 * Unwrapped group keys, cached for the tab's lifetime.
 *
 * Opening one costs an RSA decryption, and a gallery asks for the same group
 * key once per photograph. Cached here rather than re-derived, and dropped
 * wholesale on lock so nothing outlives the session it belongs to.
 */
const groupKeys = new Map<string, Uint8Array>();

const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of [...listeners]) listener();
}

/** Subscribe to lock and unlock, for `useSyncExternalStore`. */
export function subscribeKeyring(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isUnlocked(): boolean {
  return privateKey !== null;
}

export function ownPublicKey(): string | null {
  return publicKey;
}

/**
 * Build the keypair for a new account.
 *
 * Everything returned is safe to send: the public half is public, and the
 * private half is already sealed under a key the server never sees.
 */
export async function createKeyBundle(password: string): Promise<KeyBundle> {
  const salt = generateSalt();
  const iterations = 600_000;
  const passwordKey = await deriveKeyFromPassword(password, salt, iterations);
  const pair = await generateKeypair(passwordKey);

  return {
    publicKey: pair.publicKey,
    wrappedPrivateKey: pair.wrappedPrivateKey,
    keySalt: bytesToBase64(salt),
    kdfIterations: iterations,
  };
}

/**
 * Re-seal the existing private key under a new password.
 *
 * The keypair does not change, and that is the entire point: every group key
 * anybody has ever wrapped to this person's public key stays openable. Minting
 * a fresh keypair here instead would leave all of them wrapped to a public key
 * whose private half no longer exists — every photograph in every group, lost,
 * with nothing to report but a successful password change.
 *
 * The old password is needed because the key held in memory is deliberately
 * non-extractable; the bytes only exist inside the sealed blob, and only the
 * old password opens it.
 */
export async function reSealKeyring(
  bundle: KeyBundle,
  currentPassword: string,
  newPassword: string,
): Promise<{
  wrappedPrivateKey: string;
  keySalt: string;
  kdfIterations: number;
}> {
  const oldKey = await deriveKeyFromPassword(
    currentPassword,
    base64ToBytes(bundle.keySalt),
    bundle.kdfIterations,
  );

  // A new salt, because the derivation changed. Re-using it would let anyone
  // who captured both blobs attack one password twice over.
  const salt = generateSalt();
  const iterations = 600_000;
  const newKey = await deriveKeyFromPassword(newPassword, salt, iterations);

  return {
    wrappedPrivateKey: await rewrapPrivateKey(
      bundle.wrappedPrivateKey,
      oldKey,
      newKey,
    ),
    keySalt: bytesToBase64(salt),
    kdfIterations: iterations,
  };
}

/**
 * Open the keyring with the password that sealed it.
 *
 * Called at sign-in and at registration, which are the only two moments a
 * password exists in memory. A wrong password is indistinguishable from a
 * corrupted key here, and neither is worth telling apart: both mean the
 * photographs stay closed.
 */
export async function unlock(
  bundle: KeyBundle,
  password: string,
  userId?: string,
): Promise<boolean> {
  try {
    const passwordKey = await deriveKeyFromPassword(
      password,
      base64ToBytes(bundle.keySalt),
      bundle.kdfIterations,
    );
    privateKey = await unwrapPrivateKey(bundle.wrappedPrivateKey, passwordKey);
    publicKey = bundle.publicKey;

    // Best effort: a browser that refuses IndexedDB simply locks on refresh,
    // which is where this started and is survivable.
    if (userId)
      await rememberPrivateKey(userId, bundle.publicKey, privateKey).catch(
        () => {},
      );

    announce();
    return true;
  } catch {
    lock();
    return false;
  }
}

/**
 * Reopen the keyring from what this device already holds.
 *
 * Runs at start-up, before anything asks for a photograph. Returns false when
 * there is nothing stored — a different account, a cleared browser, a private
 * window — and the interface then behaves as it did before any of this: signed
 * in, photographs closed until the next sign-in.
 */
export async function restore(userId: string): Promise<boolean> {
  if (privateKey) return true;

  const stored = await recallPrivateKey(userId).catch(() => null);
  if (!stored) return false;

  privateKey = stored.privateKey;
  publicKey = stored.publicKey;
  announce();
  return true;
}

/** Forget everything. Called on sign-out, and on any failure to open. */
export function lock(): void {
  privateKey = null;
  publicKey = null;
  groupKeys.clear();
  // The stored copy goes too. A capability that outlived a sign-out on a shared
  // machine would be the one thing this is not allowed to be.
  void forgetPrivateKey().catch(() => {});
  announce();
}

/**
 * Lock, and wait until the stored copy is genuinely gone.
 *
 * `lock` is called from places that cannot await — an expired session, a
 * failure to open — and letting the delete run in the background is right for
 * those. Signing out is different: the page reloads immediately afterwards, and
 * a reload that outran the delete would leave the key on a machine somebody has
 * just signed out of.
 */
export async function forgetKeys(): Promise<void> {
  privateKey = null;
  publicKey = null;
  groupKeys.clear();
  await forgetPrivateKey().catch(() => {});
  announce();
}

/**
 * Open a group key that was sealed to this account's public half.
 *
 * The wrapped value comes from the server, which holds it and cannot read it —
 * writing a membership row grants nothing, because access is this value and
 * only a member with the group key could have produced it.
 */
export async function openGroupKey(
  groupId: string,
  wrapped: string,
): Promise<Uint8Array> {
  const cached = groupKeys.get(groupId);
  if (cached) return cached;

  if (!privateKey)
    throw new MediaCryptoError("The keyring is locked. Sign in again.");

  const key = await unwrapFromPrivateKey(wrapped, privateKey);
  groupKeys.set(groupId, key);
  return key;
}

/** Open a file key with the group key it was sealed under. */
export async function openFileKey(
  groupId: string,
  wrappedFileKey: string,
): Promise<Uint8Array> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("That group has not been opened yet.");
  }
  return unwrapWithKey(wrappedFileKey, groupKey);
}

/**
 * Make a key for a new group, sealed to this account.
 *
 * Returned wrapped and never in the open: the caller passes it to the server,
 * which stores it as the creator's grant and cannot read it. Nothing is cached
 * here — the next time the group is opened its grant is fetched and unwrapped
 * like anyone else's, so there is no second path that could drift from the
 * first.
 *
 * Throws when the keyring is locked, and that refusal matters. A group created
 * without a key is a group whose photographs are stored in the clear for ever,
 * and nothing later would make it obvious.
 */
export async function createGroupKey(
  /*
   * The recovery key, if the installation has one.
   *
   * Taken here rather than sealed afterwards because the raw group key exists
   * only inside this function — it is generated, wrapped, and dropped. Sealing
   * for escrow later would mean opening the group again, and a group created
   * while the recovery key was briefly unreachable would silently never be
   * covered.
   */
  recoveryPublicKey?: string | null,
): Promise<{ wrappedGroupKey: string; escrowedGroupKey: string | null }> {
  if (!publicKey) {
    throw new MediaCryptoError("Sign in again before creating a group.");
  }

  const groupKey = generateSymmetricKey();

  return {
    wrappedGroupKey: await wrapToPublicKey(
      groupKey,
      await importPublicKey(publicKey),
    ),
    escrowedGroupKey: recoveryPublicKey
      ? await wrapToPublicKey(groupKey, await importPublicKey(recoveryPublicKey))
      : null,
  };
}

/**
 * Seal a fresh file key with a group's key.
 *
 * The group key itself is never returned. Callers that need to encrypt ask this
 * module to do the wrapping instead of borrowing the key — so there is no path
 * by which a group key reaches a component, an event handler, or a stack trace.
 */
export async function sealFileKey(
  groupId: string,
  fileKey: Uint8Array,
): Promise<string> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("That group has not been opened yet.");
  }
  return wrapWithKey(fileKey, groupKey);
}

/**
 * The deduplication tag for a file, scoped to one group.
 *
 * Computed here for the same reason sealing is: the group key never leaves this
 * module. What the server receives is a value that identifies these bytes
 * inside this group and says nothing about them anywhere else.
 */
export async function tagForGroup(
  groupId: string,
  sha256Hex: string,
): Promise<string> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("That group has not been opened yet.");
  }
  return contentTag(sha256Hex, groupKey);
}

/**
 * How hard an invite code is to grind.
 *
 * Lower than a password's, and deliberately: a code is generated with real
 * entropy rather than chosen by a person, so the derivation is there to turn it
 * into a key of the right shape, not to buy time against guessing. Even the
 * shortest code in use carries around 59 bits, which at this cost is out of
 * reach offline. Spending 600 000 iterations on top would only make joining
 * slower on a phone.
 */
const INVITE_ITERATIONS = 210_000;

/**
 * Seal the group key so that an invite code opens it.
 *
 * This is the handover. The person following the link has no account yet and
 * therefore no keypair, so there is nothing to seal to — the code itself is the
 * only shared secret, and it is one the server never learns, because it stores
 * a hash and the code travels in the link.
 */
export async function sealGroupKeyForCode(
  groupId: string,
  code: string,
): Promise<{ wrappedGroupKey: string; keySalt: string }> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("Open the album before inviting someone to it.");
  }

  const salt = generateSalt();
  const derived = await deriveKeyFromPassword(code, salt, INVITE_ITERATIONS);

  return {
    wrappedGroupKey: await wrapWithDerivedKey(groupKey, derived),
    keySalt: bytesToBase64(salt),
  };
}

/**
 * Open an invite's copy of the group key and keep it as this account's own.
 *
 * Returns the key re-sealed to this account's public half, for the server to
 * store as the grant. The code opens the key exactly once, at this moment; from
 * then on the account's own keypair does, and the code can be exhausted or
 * expire without taking access with it.
 */
export async function adoptGroupKeyFromInvite(
  groupId: string,
  code: string,
  wrappedGroupKey: string,
  keySalt: string,
): Promise<string> {
  if (!publicKey) {
    throw new MediaCryptoError("Sign in again to finish joining.");
  }

  const derived = await deriveKeyFromPassword(
    code,
    base64ToBytes(keySalt),
    INVITE_ITERATIONS,
  );
  const groupKey = await unwrapWithDerivedKey(wrappedGroupKey, derived);

  groupKeys.set(groupId, groupKey);
  return wrapToPublicKey(groupKey, await importPublicKey(publicKey));
}

/**
 * Seal an open group key to somebody else's public half.
 *
 * The other half of `grantKeyTo`: the server can move this value around and
 * never read it, so a member joining and a member seeing are two separate
 * events joined by whoever happens to have the album open.
 */
export async function sealGroupKeyFor(
  groupId: string,
  publicKeyText: string,
): Promise<string> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("That group has not been opened yet.");
  }
  return wrapToPublicKey(groupKey, await importPublicKey(publicKeyText));
}

/** Whether a group's key is already open, so callers can avoid a round trip. */
export function hasGroupKey(groupId: string): boolean {
  return groupKeys.has(groupId);
}

/**
 * Seal arbitrary bytes with a group's key.
 *
 * The same rule as `sealFileKey`: the group key is not returned, the caller
 * asks this module to do the work. What this exists for is derived content — a
 * face embedding and the box it came from — which is not a file, has no file
 * key of its own, and is every bit as much a fact about somebody's photographs
 * as the photograph itself.
 *
 * An embedding is not a picture, and it is not harmless either. It is a durable
 * biometric identifier, and held in the clear it would let anyone with the
 * database ask "was this person in that group" — which is a question this
 * product exists to be unable to answer.
 */
export async function sealForGroup(
  groupId: string,
  plaintext: Uint8Array,
): Promise<string> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("That group has not been opened yet.");
  }
  return bytesToBase64(await encrypt(plaintext, groupKey));
}

/** Open what `sealForGroup` sealed. */
export async function openForGroup(
  groupId: string,
  sealed: string,
): Promise<Uint8Array> {
  const groupKey = groupKeys.get(groupId);
  if (!groupKey) {
    throw new MediaCryptoError("That group has not been opened yet.");
  }
  return decrypt(base64ToBytes(sealed), groupKey);
}
